import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, ChevronRight, ChevronLeft, Target, FileSearch,
  Award, BookOpen, CalendarDays, FileText, Building2,
  ScrollText, HeartHandshake, BarChart3, Bot, Bell,
  FileBarChart, ShieldCheck, Settings, Plug, UsersRound, LogOut, Sparkles, Database, X
} from 'lucide-react';
import { useUIStore } from '../../store/uiStore';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../lib/utils';
import { logoutAndCleanup } from '../../lib/session';

const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard' },
      { icon: ScrollText, label: 'What’s new', to: '/whats-new' },
    ],
  },
  {
    label: 'Business Development',
    items: [
      { icon: Target, label: 'Leads Pipeline', to: '/leads', roles: ['super_admin', 'manager', 'lead_agent'] },
      { icon: FileSearch, label: 'Tender Intelligence', to: '/tenders', roles: ['super_admin', 'manager', 'lead_agent'] },
      { icon: Building2, label: 'Opportunity Intelligence', to: '/research', roles: ['super_admin', 'manager', 'lead_agent'] },
      { icon: CalendarDays, label: 'Event Intelligence', to: '/events', roles: ['super_admin', 'manager', 'lead_agent'] },

    ],
  },
  {
    label: 'AI Agents',
    items: [
      { icon: Bot, label: 'Run AI Agent', to: '/agents', roles: ['super_admin', 'manager', 'lead_agent'] },
      { icon: Sparkles, label: 'Agent History', to: '/agents/history' },
    ],
  },

  {
    label: 'Communications',
    items: [
      { icon: Bell, label: 'Notifications', to: '/notifications' },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { icon: FileBarChart, label: 'Reports', to: '/reports' },
      { icon: ShieldCheck, label: 'Audit Log', to: '/audit', roles: ['super_admin', 'auditor'] },
      { icon: BarChart3, label: 'Admin Dashboard', to: '/admin', roles: ['super_admin'] },
    ],
  },
  {
    label: 'Settings',
    items: [
      { icon: UsersRound, label: 'Manage Users', to: '/admin/users', roles: ['super_admin'] },
      { icon: Plug, label: 'Integrations', to: '/settings/integrations', roles: ['super_admin'] },
      { icon: Database, label: 'Evidence & Expertise', to: '/admin/evidence-repositories', roles: ['super_admin', 'manager'] },
      { icon: Settings, label: 'Profile', to: '/settings/profile' },
    ],
  },
];

import { useProductUpdates } from '../../features/product-updates/useProductUpdates';

export default function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { unread } = useProductUpdates();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => logoutAndCleanup(navigate);

  const canSee = (roles?: string[]) => !roles || !user || roles.includes(user.role);

  return (
    <div className="h-full flex flex-col bg-lum-gradient shadow-xl select-none">
      {/* Logo area */}
      <div className={cn('flex items-center h-header px-4 border-b border-white/10 flex-shrink-0',
        sidebarCollapsed ? 'justify-center' : 'justify-between')}>
        {!sidebarCollapsed && (
          <div className="flex items-center gap-2 min-w-0">
            <img src="/lumora-logo-refined.svg"
              alt="Lumora" className="h-7 w-auto flex-shrink-0" />
          </div>
        )}
        <button
          onClick={onNavigate ?? toggleSidebar}
          className="p-1.5 rounded-lum text-white/60 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
          title={onNavigate ? 'Close menu' : sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={onNavigate ? 'Close menu' : sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {onNavigate ? <X size={18} /> : sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Nav sections */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {NAV_SECTIONS.map((section) => {
          const visibleItems = section.items.filter((item) => canSee((item as { roles?: string[] }).roles));
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.label} className="mb-2">
              {!sidebarCollapsed && (
                <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/30 mb-1">
                  {section.label}
                </p>
              )}
              {visibleItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-tour={item.to === '/whats-new' ? 'nav-whats-new' : undefined}
                  onClick={onNavigate}
                  end={item.to === '/admin' || item.to === '/agents'}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn('sidebar-link', (isActive || (item.to === '/research' && location.pathname === '/criteria-requirements')) && 'active', sidebarCollapsed && 'justify-center px-0')
                  }
                >
                  <item.icon size={18} className="flex-shrink-0 sidebar-icon" />
                  {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                  {item.to === '/whats-new' && unread && <span aria-label="New updates available" className={sidebarCollapsed ? 'h-2.5 w-2.5 rounded-full border border-red-600 bg-red-600 shrink-0' : 'ml-auto shrink-0 rounded-full border border-red-600 bg-red-600 px-2 py-0.5 text-[10px] font-semibold text-white'}>{!sidebarCollapsed && 'Updated'}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className={cn('border-t border-white/10 p-3 flex-shrink-0',
        sidebarCollapsed ? 'flex justify-center' : 'block')}>
        {!sidebarCollapsed && user && (
          <div className="flex items-center gap-2.5 px-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center text-navy text-xs font-bold flex-shrink-0">
              {user.profile.firstName[0]}{user.profile.lastName[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-semibold truncate">
                {user.profile.firstName} {user.profile.lastName}
              </p>
              <p className="text-white/50 text-xs capitalize truncate">
                {user.role.replace(/_/g, ' ')}
              </p>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={cn('sidebar-link w-full', sidebarCollapsed && 'justify-center px-0')}
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!sidebarCollapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );
}
